import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { navigateTo, RedirectError } from "bunfx";
import { useEffect, useRef } from "preact/hooks";
import {
  IcoArchive,
  IcoCheck,
  IcoChevronLeft,
  IcoEye,
  IcoUser,
} from "../../components/icons";
import {
  BrandedLayout,
  Button,
  ConfirmDialog,
  RadioCard,
  RadioCardContent,
} from "../../components/ui";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import { showError } from "../../stores/errors";
import type { MimicState } from "../../stores/mimic";
import type { BrandColor, MemberRole, OrgMember, User } from "../../types";
import { isAdmin } from "../../types";
import { getErrorMessage } from "../../utils/errors";

type PotentialManager = {
  id: string;
  name: string;
  displayName: string;
};

/** Truncate long email addresses for display in dropdowns */
function truncateEmail(email: string, maxLength = 30): string {
  if (email.length <= maxLength) return email;
  const atIndex = email.indexOf("@");
  if (atIndex === -1) return `${email.slice(0, maxLength - 3)}...`;
  const [local, domain] = [email.slice(0, atIndex), email.slice(atIndex + 1)];
  // Keep first 10 chars of local + ... + @domain
  const maxLocalLength = Math.max(10, maxLength - domain.length - 4);
  if (local.length > maxLocalLength) {
    return `${local.slice(0, maxLocalLength)}...@${domain}`;
  }
  return email;
}

type MemberAdminState = {
  currentUser: User;
  mimic: MimicState;
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
  member: OrgMember;
  potentialManagers: PotentialManager[];
  hasReports: boolean;
  isCurrentUserAdmin: boolean;
  isCurrentMember: boolean;
};

export async function load(args: LoaderArgs): Promise<MemberAdminState> {
  const orgId = args.params.orgId ?? "";
  const memberId = args.params.memberId ?? "";

  const [authResult, orgsResult, orgResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
    rpc.orgs.get({ id: orgId }),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  // Only admins can view member details
  if (!isAdmin(orgResult.currentMember.role)) {
    throw new RedirectError("/");
  }

  const member = orgResult.organization.members.find((m) => m.id === memberId);
  if (!member) {
    throw new RedirectError(`/orgs/${orgId}/members`);
  }

  // Get brand color from list endpoint
  const orgFromList = orgsResult.organizations.find((o) => o.id === orgId);
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  // Build list of potential managers: active members excluding the current member
  const potentialManagers: PotentialManager[] = orgResult.organization.members
    .filter(
      (m) => m.id !== memberId && m.acceptedAt && !m.archivedAt && !m.leftAt,
    )
    .map((m) => {
      const name = m.userName || m.userEmail;
      return {
        id: m.id,
        name,
        displayName: m.userName || truncateEmail(m.userEmail),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    orgId,
    orgName: orgResult.organization.name,
    brandColor,
    member,
    potentialManagers,
    hasReports: orgFromList?.hasReports ?? false,
    isCurrentUserAdmin: isAdmin(orgResult.currentMember.role),
    isCurrentMember: orgResult.currentMember.id === memberId,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  const {
    currentUser,
    mimic,
    orgId,
    orgName,
    brandColor,
    potentialManagers,
    hasReports,
    isCurrentUserAdmin,
    isCurrentMember,
  } = state.value;

  usePageTitle(`Manage ${state.value.member.userName ?? "Member"}`);

  const member = useSignal(state.value.member);
  const isUpdating = useSignal(false);
  const saveStatus = useSignal<"idle" | "saved">("idle");
  const saveStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveStatusTimeoutRef.current) {
        clearTimeout(saveStatusTimeoutRef.current);
      }
    };
  }, []);

  // Show "Saved" feedback briefly
  function showSaved() {
    if (saveStatusTimeoutRef.current) {
      clearTimeout(saveStatusTimeoutRef.current);
    }
    saveStatus.value = "saved";
    saveStatusTimeoutRef.current = setTimeout(() => {
      saveStatus.value = "idle";
      saveStatusTimeoutRef.current = null;
    }, 2000);
  }

  const isPending = !member.value.acceptedAt;
  const isArchived = Boolean(member.value.archivedAt || member.value.leftAt);
  const displayName = member.value.userName || member.value.userEmail;
  const canManage = isCurrentUserAdmin && !isCurrentMember;

  async function handleRoleChange(newRole: MemberRole) {
    if (isUpdating.value || newRole === member.value.role) return;
    isUpdating.value = true;
    try {
      await rpc.orgs.updateMemberRole({
        orgId,
        memberId: member.value.id,
        role: newRole,
      });
      member.value = { ...member.value, role: newRole };
      showSaved();
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isUpdating.value = false;
    }
  }

  async function handleArchive(archive: boolean) {
    if (isUpdating.value) return;
    isUpdating.value = true;
    try {
      await rpc.orgs.setMemberArchived({
        orgId,
        memberId: member.value.id,
        archived: archive,
      });
      navigateTo(`/orgs/${orgId}/members`);
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isUpdating.value = false;
    }
  }

  async function handleManagerChange(managerId: string | null) {
    if (isUpdating.value) return;
    const previousManagerId = member.value.managerId;
    isUpdating.value = true;
    // Optimistic update
    member.value = { ...member.value, managerId };
    try {
      await rpc.orgs.setMemberManager({
        orgId,
        memberId: member.value.id,
        managerId,
      });
      showSaved();
    } catch (err) {
      // Rollback on error
      member.value = { ...member.value, managerId: previousManagerId };
      showError(getErrorMessage(err));
    } finally {
      isUpdating.value = false;
    }
  }

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={orgId}
      orgName={orgName}
      brandColor={brandColor}
      activeTab="team"
      isAdmin={isCurrentUserAdmin}
      hasReports={hasReports}
    >
      <div class="flex justify-center">
        <div class="w-full max-w-lg flex flex-col gap-6">
          <div class="flex items-center justify-between">
            <a
              href={`/orgs/${orgId}/members`}
              class="inline-flex items-center gap-1 text-sm font-medium text-text-muted hover:text-text"
            >
              <IcoChevronLeft class="-ml-[5px] size-4" />
              Back to Members
            </a>
            <div class="flex items-center gap-4">
              {saveStatus.value === "saved" && (
                <span class="inline-flex items-center gap-1 text-sm font-medium text-green-600 dark:text-green-400">
                  <IcoCheck class="size-4" />
                  Saved
                </span>
              )}
              <a
                href={`/orgs/${orgId}/members/${member.value.id}`}
                class="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80"
              >
                View Profile
              </a>
            </div>
          </div>

          {/* Profile Header */}
          <div class="flex items-center gap-5">
            <div class="size-16 rounded-full flex items-center justify-center bg-surface-muted">
              <IcoUser
                class={`size-8 ${isPending || isArchived ? "text-text-subtle" : "text-text-muted"}`}
              />
            </div>
            <div class="flex flex-col gap-1">
              <div class="flex items-center gap-2 flex-wrap">
                <h1
                  class={`text-2xl font-bold ${isPending || isArchived ? "text-text-muted" : "text-text"}`}
                >
                  {displayName}
                </h1>
                <StatusBadge
                  role={member.value.role}
                  isPending={isPending}
                  isArchived={isArchived}
                  hasLeft={Boolean(member.value.leftAt)}
                />
              </div>
              {member.value.userName && (
                <p class="text-text-muted">{member.value.userEmail}</p>
              )}
            </div>
          </div>

          {/* Admin Actions */}
          {canManage && !isArchived && (
            <RoleSection role={member.value.role} onChange={handleRoleChange} />
          )}

          {canManage && !isArchived && (
            <ManagerSection
              managerId={member.value.managerId ?? null}
              potentialManagers={potentialManagers}
              onChange={handleManagerChange}
              isUpdating={isUpdating.value}
            />
          )}

          {canManage && (
            <ArchiveSection
              isArchived={isArchived}
              isUpdating={isUpdating.value}
              memberName={displayName}
              onArchive={handleArchive}
            />
          )}

          {/* View as this user (mimic mode) - only for active members */}
          {canManage && !isPending && !isArchived && (
            <ViewAsSection
              memberId={member.value.id}
              memberName={displayName}
              orgId={orgId}
            />
          )}
        </div>
      </div>
    </BrandedLayout>
  );
}

function StatusBadge({
  role,
  isPending,
  isArchived,
  hasLeft,
}: {
  role: MemberRole;
  isPending: boolean;
  isArchived: boolean;
  hasLeft?: boolean;
}) {
  if (isPending) {
    return (
      <span class="text-xs font-medium px-2 py-1 rounded bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300">
        Pending
      </span>
    );
  }

  if (isArchived) {
    return (
      <span class="text-xs font-medium px-2 py-1 rounded bg-surface-alt text-text-muted">
        {hasLeft ? "Left" : "Archived"}
      </span>
    );
  }

  if (role === "admin") {
    return (
      <span class="text-xs font-medium px-2 py-1 rounded bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300">
        Admin
      </span>
    );
  }

  return null;
}

function RoleSection({
  role,
  onChange,
}: {
  role: MemberRole;
  onChange: (role: MemberRole) => void;
}) {
  return (
    <fieldset class="flex flex-col gap-2">
      <legend class="text-sm font-medium text-text-secondary">Role</legend>
      <div class="flex gap-3">
        <RadioCard
          name="role"
          value="member"
          checked={role === "member"}
          onChange={() => onChange("member")}
          compact
          class="flex-1"
        >
          <RadioCardContent
            label="Member"
            description="Can view and submit forms"
          />
        </RadioCard>
        <RadioCard
          name="role"
          value="admin"
          checked={role === "admin"}
          onChange={() => onChange("admin")}
          compact
          class="flex-1"
        >
          <RadioCardContent
            label="Admin"
            description="Can manage members and forms"
          />
        </RadioCard>
      </div>
    </fieldset>
  );
}

function ArchiveSection({
  isArchived,
  isUpdating,
  memberName,
  onArchive,
}: {
  isArchived: boolean;
  isUpdating: boolean;
  memberName: string;
  onArchive: (archive: boolean) => void;
}) {
  const showConfirm = useSignal(false);

  if (isArchived) {
    return (
      <div class="pt-4 border-t border-border">
        <Button
          variant="secondary"
          onClick={() => onArchive(false)}
          loading={isUpdating}
        >
          Restore membership
        </Button>
      </div>
    );
  }

  return (
    <div class="pt-4 border-t border-border">
      <button
        type="button"
        onClick={() => {
          showConfirm.value = true;
        }}
        disabled={isUpdating}
        class="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-base font-medium text-rose-600 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/30 dark:hover:bg-rose-950/50 transition-colors disabled:opacity-50"
      >
        <IcoArchive class="size-4" />
        Archive member
      </button>
      <ConfirmDialog
        open={showConfirm.value}
        title="Archive Member"
        confirmLabel="Archive"
        onConfirm={() => {
          showConfirm.value = false;
          onArchive(true);
        }}
        onCancel={() => {
          showConfirm.value = false;
        }}
      >
        Are you sure you want to archive {memberName}? They will lose access to
        this organization.
      </ConfirmDialog>
    </div>
  );
}

function ManagerSection({
  managerId,
  potentialManagers,
  onChange,
  isUpdating,
}: {
  managerId: string | null;
  potentialManagers: PotentialManager[];
  onChange: (managerId: string | null) => void;
  isUpdating: boolean;
}) {
  return (
    <div class="flex flex-col gap-2">
      <label
        for="manager-select"
        class="text-sm font-medium text-text-secondary"
      >
        Manager
      </label>
      <select
        id="manager-select"
        value={managerId ?? ""}
        onChange={(e) => {
          const value = (e.target as HTMLSelectElement).value;
          onChange(value === "" ? null : value);
        }}
        disabled={isUpdating}
        class={`w-full px-3 py-2 text-base rounded-lg border border-border bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${isUpdating ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <option value="">No manager</option>
        {potentialManagers.map((m) => (
          <option key={m.id} value={m.id} title={m.name}>
            {m.displayName}
          </option>
        ))}
      </select>
      <p class="text-xs text-text-muted">
        The manager will be able to review this member's check-ins.
      </p>
    </div>
  );
}

function ViewAsSection({
  memberId,
  memberName,
  orgId,
}: {
  memberId: string;
  memberName: string;
  orgId: string;
}) {
  const isStarting = useSignal(false);

  async function handleStartMimic() {
    if (isStarting.value) return;
    isStarting.value = true;
    try {
      await rpc.auth.startMimic({
        workspaceId: orgId,
        targetMemberId: memberId,
      });
      // Redirect to home to see their view
      navigateTo("/");
      window.location.reload();
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isStarting.value = false;
    }
  }

  return (
    <div class="pt-4 border-t border-border">
      <button
        type="button"
        onClick={handleStartMimic}
        disabled={isStarting.value}
        class="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-base font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/30 dark:text-amber-300 dark:hover:bg-amber-950/50 transition-colors disabled:opacity-50"
      >
        <IcoEye class="size-4" />
        {isStarting.value ? "Starting..." : `View as ${memberName}`}
      </button>
      <p class="text-xs text-text-muted mt-2">
        See the app exactly as this user sees it. You'll be in read-only mode.
      </p>
    </div>
  );
}
